// Geocodificação para o item 2.70: CEP/endereço <-> coordenadas.
// - CEP -> endereço: ViaCEP (grátis).
// - Endereço <-> coordenadas: Google Geocoding (quando há VITE_GOOGLE_MAPS_API_KEY)
//   com melhor cobertura de número/CEP no Brasil; cai para Nominatim (OSM) sem a chave.

/* eslint-disable @typescript-eslint/no-explicit-any */

export interface EnderecoCep {
  logradouro: string;
  bairro: string;
  cidade: string;
  uf: string;
  cep: string;
}

/** Consulta o ViaCEP e retorna o endereço (sem número). */
export async function buscarCep(cep: string): Promise<EnderecoCep | null> {
  const limpo = cep.replace(/\D/g, '');
  if (limpo.length !== 8) return null;
  try {
    const r = await fetch(`https://viacep.com.br/ws/${limpo}/json/`);
    const d = await r.json();
    if (d.erro) return null;
    return {
      logradouro: d.logradouro ?? '',
      bairro: d.bairro ?? '',
      cidade: d.localidade ?? '',
      uf: d.uf ?? '',
      cep: limpo,
    };
  } catch {
    return null;
  }
}

export interface ConsultaEndereco {
  logradouro?: string;
  numero?: string;
  bairro?: string;
  cidade?: string;
  uf?: string;
  cep?: string;
}

export interface EnderecoReverso {
  logradouro: string;
  numero: string;
  bairro: string;
  cidade: string;
  uf: string;
  cep: string;
}

// ---------------------------------------------------------------------------
// Google Maps JavaScript API (Geocoder) — carregado sob demanda quando há chave.
// (As Web Service REST não suportam CORS no navegador; por isso usamos o SDK JS.)
// ---------------------------------------------------------------------------
const GOOGLE_KEY = (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined)?.trim() || '';

/** Há chave do Google configurada? (decide se o mapa usa Google ou cai no Leaflet). */
export const TEM_GOOGLE_MAPS = !!GOOGLE_KEY;

let googlePromise: Promise<any | null> | null = null;

/** Carrega o SDK do Google Maps sob demanda (uma vez). Retorna `google.maps` ou null. */
export function carregarGoogleMaps(): Promise<any | null> {
  if (!GOOGLE_KEY || typeof window === 'undefined') return Promise.resolve(null);
  const w = window as any;
  if (w.google?.maps) return Promise.resolve(w.google.maps);
  if (googlePromise) return googlePromise;
  googlePromise = new Promise((resolve) => {
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_KEY}&language=pt-BR&region=BR`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve((window as any).google?.maps ?? null);
    s.onerror = () => resolve(null);
    document.head.appendChild(s);
  });
  return googlePromise;
}

const carregarGoogle = carregarGoogleMaps;

async function googleGeocode(req: any): Promise<any[] | null> {
  const maps = await carregarGoogle();
  if (!maps) return null;
  try {
    const geocoder = new maps.Geocoder();
    const resp = await geocoder.geocode(req);
    return resp?.results ?? null;
  } catch {
    return null;
  }
}

function parseGoogle(comps: any[]): EnderecoReverso {
  const get = (type: string) => comps.find((c) => (c.types as string[]).includes(type));
  return {
    logradouro: get('route')?.long_name ?? '',
    numero: get('street_number')?.long_name ?? '',
    bairro: get('sublocality_level_1')?.long_name ?? get('sublocality')?.long_name ?? get('neighborhood')?.long_name ?? '',
    cidade: get('administrative_area_level_2')?.long_name ?? get('locality')?.long_name ?? '',
    uf: get('administrative_area_level_1')?.short_name ?? '',
    cep: (get('postal_code')?.long_name ?? '').replace(/\D/g, ''),
  };
}

/**
 * Converte um endereço em coordenadas (geocodificação direta).
 * Usa Google quando disponível (acha número de fachada); senão Nominatim
 * estruturado (respeita cidade/UF; precisão por número depende do OSM).
 */
export async function geocodificarEndereco(q: ConsultaEndereco): Promise<{ lat: number; lng: number } | null> {
  const street = [q.numero, q.logradouro].map((s) => (s ?? '').trim()).filter(Boolean).join(' ');
  const cep = (q.cep ?? '').replace(/\D/g, '');
  if (!street && !cep) return null;

  // Google primeiro.
  if (GOOGLE_KEY) {
    const cidadeUf = q.cidade && q.uf ? `${q.cidade} - ${q.uf}` : (q.cidade || q.uf || '');
    const endereco = [[q.logradouro, q.numero].filter(Boolean).join(', '), q.bairro, cidadeUf, cep, 'Brasil']
      .map((s) => (s ?? '').trim()).filter(Boolean).join(', ');
    const results = await googleGeocode({ address: endereco, region: 'BR' });
    if (results && results[0]?.geometry?.location) {
      const l = results[0].geometry.location;
      return { lat: l.lat(), lng: l.lng() };
    }
  }

  // Fallback Nominatim (estruturado).
  const params = new URLSearchParams({ format: 'jsonv2', limit: '1', countrycodes: 'br', addressdetails: '0' });
  if (street) params.set('street', street);
  if (q.cidade) params.set('city', q.cidade);
  if (q.uf) params.set('state', q.uf);
  if (cep) params.set('postalcode', cep);
  try {
    const r = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`, {
      headers: { 'Accept-Language': 'pt-BR' },
    });
    const j = await r.json();
    if (Array.isArray(j) && j.length > 0 && j[0].lat && j[0].lon) {
      return { lat: parseFloat(j[0].lat), lng: parseFloat(j[0].lon) };
    }
  } catch {
    /* ignora */
  }
  return null;
}

/** Converte coordenadas em endereço completo (geocodificação reversa). */
export async function reverseGeocode(lat: number, lng: number): Promise<EnderecoReverso | null> {
  // Google primeiro (traz número de fachada e CEP onde existir).
  if (GOOGLE_KEY) {
    const results = await googleGeocode({ location: { lat, lng } });
    if (results && results[0]?.address_components) {
      return parseGoogle(results[0].address_components);
    }
  }

  // Fallback Nominatim.
  try {
    const r = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&addressdetails=1&zoom=18`,
      { headers: { 'Accept-Language': 'pt-BR' } },
    );
    const j = await r.json();
    const a = (j.address ?? {}) as Record<string, string>;
    const iso = a['ISO3166-2-lvl4'];
    return {
      logradouro: a.road ?? a.pedestrian ?? a.footway ?? '',
      numero: a.house_number ?? '',
      bairro: a.suburb ?? a.neighbourhood ?? a.city_district ?? '',
      cidade: a.city ?? a.town ?? a.village ?? a.municipality ?? '',
      uf: iso && iso.includes('-') ? iso.split('-')[1] : '',
      cep: (a.postcode ?? '').replace(/\D/g, ''),
    };
  } catch {
    return null;
  }
}
